import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

const Enrol = ({ setup, onVerified }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post('/api/mfa/verify', { code }); onVerified(data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Invalid code – try again'); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={verify} className="space-y-2 rounded-md border p-3" data-testid="platform-mfa-enrol">
      <p>Scan this QR code with Google Authenticator, Microsoft Authenticator or 1Password, then enter the 6-digit code it shows.</p>
      <img src={setup.qr} alt="Authenticator QR code" className="h-36 w-36" data-testid="platform-mfa-qr" />
      <p className="break-all font-mono text-xs" data-testid="platform-mfa-secret">Or enter this key manually: {setup.secret}</p>
      <div className="flex gap-2">
        <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-32 font-mono tracking-widest" data-testid="platform-mfa-code" />
        <Button type="submit" size="sm" disabled={code.length !== 6 || busy} data-testid="platform-mfa-verify">Turn on</Button>
      </div>
    </form>
  );
};

// Platform admin MFA: status, enrolment (if not yet set up) and backup-code regeneration.
export const PlatformMfaCard = ({ onEnabled }) => {
  const [st, setSt] = useState(null);
  const [codes, setCodes] = useState(null);
  const [setup, setSetup] = useState(null);
  useEffect(() => { api.get('/api/mfa/status').then((r) => setSt(r.data)).catch(() => {}); }, []);
  const regen = async () => {
    try { const { data } = await api.post('/api/mfa/backup-codes/regenerate'); setCodes(data.backup_codes); toast.success('New backup codes generated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not regenerate codes'); }
  };
  const start = async () => {
    try { const { data } = await api.post('/api/mfa/setup'); setSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start setup'); }
  };
  const verified = (data) => {
    if (data.access_token) sessionStorage.setItem('access_token', data.access_token);
    setSetup(null); setCodes(data.backup_codes || null); setSt((s) => ({ ...s, enabled: true }));
    toast.success('Authenticator app turned on');
    onEnabled?.();
  };
  return (
    <Card data-testid="platform-mfa-card"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" />Two-factor authentication</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>Authenticator app: <Badge variant="outline" data-testid="platform-mfa-status">{st?.enabled ? 'Enabled' : 'Not enabled'}</Badge>
          <span className="ml-2 text-xs text-muted-foreground">Mandatory for platform administrators in every environment.</span></div>
        {st && !st.enabled && !setup && <Button size="sm" onClick={start} data-testid="platform-mfa-start">Set up authenticator app</Button>}
        {setup && <Enrol setup={setup} onVerified={verified} />}
        <Button size="sm" variant="outline" onClick={regen} disabled={!st?.enabled} data-testid="platform-mfa-regen">Regenerate backup codes</Button>
        {codes && <div className="grid grid-cols-2 gap-1 rounded-md border p-3 font-mono text-xs md:grid-cols-5" data-testid="platform-mfa-codes">{codes.map((c) => <span key={c}>{c}</span>)}
          <p className="col-span-full mt-1 font-sans text-muted-foreground">Shown once. Store them somewhere safe; each works once.</p></div>}
      </CardContent></Card>
  );
};
